import apiEndPointsConfig from "../services/apiEndPoints";

// Client-side context for the Activity Log (audit trail). Two headers are sent with API calls:
//   X-Client-Timezone  — the browser's IANA timezone (always available, no permission needed)
//   X-Client-Location  — "<lat>,<lng>,<accuracyMeters>,<capturedAtMs>" from the Geolocation API,
//                        ONLY after the user has granted the browser's location permission.
// The client IP is NOT sent from here — the API resolves it from the incoming connection.

export const getClientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

// ── Geolocation ──────────────────────────────────────────────────────────────────────────
// When the permission prompt is shown ("where appropriate"):
//   - on the login form submit — a direct user action, so the browser prompt makes sense in
//     context; a denial is final (the browser remembers it) and the app never re-asks.
//   - never on plain page loads. Pages only silently refresh the position when the permission
//     is already "granted".
// The last fix is kept in sessionStorage (cleared on logout / when the tab closes) — never
// persisted long-term in the browser.

const LOCATION_STORAGE_KEY = "activityLogClientLocation";
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 5 * 60 * 1000 };

const geolocationAvailable = () =>
  typeof window !== "undefined" && window.isSecureContext !== false && typeof navigator !== "undefined" && !!navigator.geolocation;

const storeFix = (position) => {
  const { latitude, longitude, accuracy } = position?.coords || {};
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
  const fix = { latitude, longitude, accuracy: Number.isFinite(accuracy) ? accuracy : null, capturedAt: position.timestamp || Date.now() };
  try {
    sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(fix));
  } catch {
    // storage blocked — location is simply not sent
  }
};

export const clearClientLocation = () => {
  try {
    sessionStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch {
    // ignore
  }
};

// Header value for X-Client-Location, or "" when no fix is available.
export const getClientLocationHeader = () => {
  try {
    const fix = JSON.parse(sessionStorage.getItem(LOCATION_STORAGE_KEY) || "null");
    if (!fix) return "";
    return [fix.latitude.toFixed(6), fix.longitude.toFixed(6), fix.accuracy === null ? "" : Math.round(fix.accuracy), fix.capturedAt].join(",");
  } catch {
    return "";
  }
};

export const getLocationPermissionState = async () => {
  try {
    if (!navigator.permissions?.query) return "unknown";
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state; // "granted" | "denied" | "prompt"
  } catch {
    return "unknown";
  }
};

// prompt=true may show the browser's permission dialog (call it from a user action);
// prompt=false only reads the position if permission was already granted.
export const requestClientLocation = async ({ prompt = false } = {}) => {
  if (!geolocationAvailable()) return false;
  const state = await getLocationPermissionState();
  if (state === "denied") {
    clearClientLocation();
    return false;
  }
  if (!prompt && state !== "granted") return false;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        storeFix(position);
        resolve(true);
      },
      () => resolve(false), // denied / unavailable / timeout — logging continues without coordinates
      GEO_OPTIONS
    );
  });
};

// Keeps the stored fix fresh while the app is open (only when permission is already granted).
// Returns a cleanup function for useEffect.
export const startClientLocationRefresh = () => {
  requestClientLocation({ prompt: false });
  const timer = setInterval(() => requestClientLocation({ prompt: false }), REFRESH_INTERVAL_MS);
  return () => clearInterval(timer);
};

// Headers attached to every API call (see axiosInterceptors.js).
export const getActivityContextHeaders = () => {
  const headers = {};
  const timezone = getClientTimezone();
  if (timezone) headers["X-Client-Timezone"] = timezone;
  const location = getClientLocationHeader();
  if (location) headers["X-Client-Location"] = location;
  return headers;
};

// Tells the API a logout happened so it lands in the Activity Log (JWTs are stateless, so the
// server can't otherwise know). Fire-and-forget: `keepalive` lets the request finish even
// though the page navigates away, and using fetch (not axiosInstance) keeps a 401 for an
// already-expired token from triggering the interceptor's redirect logic mid-logout.
export const recordLogout = () => {
  const token = localStorage.getItem("token");
  if (!token) return;
  try {
    fetch(apiEndPointsConfig.logout, {
      method: "POST",
      keepalive: true,
      headers: {
        Authorization: `Bearer ${token}`,
        ...getActivityContextHeaders(),
      },
    }).catch(() => {});
  } catch {
    // logging out must never be blocked by audit reporting
  } finally {
    clearClientLocation();
  }
};
